import React, { useContext } from 'react';
import {
  Banner,
  Body,
  Button,
  Group,
  H7,
  Icon,
  Stack,
} from '@qred/components-library';
import useTranslate from '~/hooks/useTranslate';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { RootState } from '~/store';
import useMarketConfig from '~/hooks/useMarketConfig';
import useModals from '~/hooks/useModals';
import { updateForm } from '~/store/slices/cardOffer.slice';
import { useCardOfferSelectors } from '~/store/selectors/cardOffer.selector';
import OwnersList from './OwnersList';
import OwnerManagementModal from './OwnerManagementModal';
import { ValidationContext } from '~/components/hoc/withValidation';
import { trackCardOfferActions } from '~/store/actions/analyticsActions';

const OwnershipAdditionalOwnersPanel = () => {
  const dispatch = useAppDispatch();
  const {
    cardOffer: {
      form: { companyHasOtherBeneficialOwners, owners },
    },
    matchedMedia: { mobile },
  } = useSelector((state: RootState) => state);
  const validationContext = useContext(ValidationContext);

  const t = useTranslate();
  const modals = useModals();
  const {
    applicantHasAnsweredBeneficialOwnersQuestion,
    totalAmountOfShares,
    applicantIsABeneficialOwner,
    alternativeOwnerManuallyAdded,
  } = useCardOfferSelectors();

  const { marketHasPersonalNumber } = useMarketConfig();

  const onChange = (otherBeneficialOwnersExist: boolean) => {
    dispatch(
      updateForm({
        companyHasOtherBeneficialOwners: otherBeneficialOwnersExist,
      })
    );
  };

  const openOwnerManagementModal = () => {
    dispatch(
      trackCardOfferActions({
        actionName: 'card_offer_add_owner',
      })
    );

    modals.openModal({
      header: <H7>{t('OnboardingOffer.AddNewOwner')}</H7>,
      children: (
        <OwnerManagementModal
          isAlternativeBeneficialOwner={!companyHasOtherBeneficialOwners}
          askForPersonalNumber={marketHasPersonalNumber}
          askForDateOfBirth={!marketHasPersonalNumber}
        />
      ),
      size: 'md',
      fullScreen: mobile,
      filledBackground: true,
      hasOwnFooter: true,
    });
  };

  const applicantCanAddAdditionalBeneficialOwners =
    companyHasOtherBeneficialOwners && totalAmountOfShares < 75;

  const applicantCanAddAlternativeOwners =
    !companyHasOtherBeneficialOwners && !applicantIsABeneficialOwner;

  return (
    <Stack>
      <Body size="lg">
        {t('OnboardingOffer.AdditionalOwnersPanelDescription')}
      </Body>
      <Group grow>
        <Button
          onClick={() => onChange(true)}
          variant="secondary"
          data-active={companyHasOtherBeneficialOwners}
        >
          {t('Yes')}
        </Button>
        <Button
          onClick={() => onChange(false)}
          variant="secondary"
          data-active={
            applicantHasAnsweredBeneficialOwnersQuestion &&
            !companyHasOtherBeneficialOwners
          }
        >
          {t('No')}
        </Button>
      </Group>
      {applicantHasAnsweredBeneficialOwnersQuestion && (
        <>
          {applicantCanAddAdditionalBeneficialOwners && (
            <Body size="lg">
              {t('OnboardingOffer.AdditionalOwnersPanelAddOwnerLabel')}
            </Body>
          )}

          {applicantCanAddAlternativeOwners && (
            <Body size="lg">
              {t(
                'OnboardingOffer.AdditionalOwnersPanelAddAlternativeBeneficialOwner'
              )}
            </Body>
          )}

          {!!owners.length && <OwnersList />}

          <Banner
            state="critical"
            withCloseButton={false}
            message={t('OnboardingOffer.TotalOwnershipSharesMoreThan100')}
            opened={
              validationContext.validationErrors.totalOwnershipSharesMoreThan100
            }
          />

          {(applicantCanAddAdditionalBeneficialOwners ||
            applicantCanAddAlternativeOwners) && (
            <Stack align="center">
              <Button
                onClick={openOwnerManagementModal}
                variant="secondary"
                fullWidth={mobile}
                disabled={alternativeOwnerManuallyAdded}
              >
                <Icon src="Plus" mr="0.5em" />
                {t('OnboardingOffer.AdditionalOwnersPanelAddOwnerButtonLabel')}
              </Button>
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};

export default OwnershipAdditionalOwnersPanel;
